import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@components/ui/button'
import {useChatbotStore} from '@features/chatbot/store/chatbotStore.ts'
import {httpGetAssignment, httpPullChatbotAssignments} from '@features/assignment/http/assignment.http'
import {capitalize, cn, errorHandler, raise} from '@utilities/helpers.ts'
import {CHATBOT_SOURCES, ChatbotAssignmentTaskStatusArray} from '@features/chatbot/types.ts'
import {Assignment} from '@features/assignment/types.ts'

interface PullShootsProps {
    flowSource: string
    statuses: ChatbotAssignmentTaskStatusArray
    filterByDate: '0' | '1'
}

const shootClassName =
    'bg-white py-1.5 px-4 shadow-[rgb(0_0_0/5%)_0_4px_8px_0] text-center text-sm font-semibold cursor-pointer transition duration-200 ease-in-out grid items-center gap-2 [&>span]:col-span-2 [&>span]:justify-start [&>span]:text-start [&>span]:text-danger-500 [&>span]:font-bold [&_p]:grid [&_p]:row-start-2 [&_p]:grid-cols-[auto_1fr] [&_p]:gap-1 [&_p]:text-left [&_p_span]:font-normal [&_p_span]:max-w-20 [&_p_span]:overflow-hidden [&_p_span]:whitespace-nowrap [&_p_span]:text-ellipsis hover:bg-neutral-100'

export const PullShoots = ({flowSource, statuses, filterByDate}: PullShootsProps) => {
    const {t} = useTranslation()
    const setSelectedAssignment = useChatbotStore(store => store.setSelectedAssignment)
    const addStepToChatHistory = useChatbotStore(store => store.addStepToHistory)
    const chatbotRef = useChatbotStore(store => store.chatbotRef)
    const [shoots, setShoots] = useState<Assignment[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [selectedShoot, setSelectedShoot] = useState<Assignment | null>(null)

    useEffect(() => {
        onGetShootsResults()
    }, [])

    const onGetShootsResults = async () => {
        setIsLoading(true)
        try {
            const {data} = await httpPullChatbotAssignments({
                params: {statuses, response_type: 'extended', filter_by_date: filterByDate}
            })
            setShoots(data as Assignment[])
        } catch (e) {
            errorHandler(e)
        } finally {
            setIsLoading(false)
        }
    }

    // Based on the arrival flow, return the step to trigger if the user found the shoot in the list
    const pullShootsTriggerFound = (flowSource: string) => {
        switch (flowSource) {
            case 'MB1_yes_pull':
                return 'MB1_yes_br'
            case 'MB1_no_pull':
                return 'MB1_no_br'
            case 'MB1.2_pull':
                return 'MB1.2_cancel'
            case 'MB1.3_pull':
                return 'MB1.3.1'
            case 'MB1.4_pull':
                return 'MB1.4.1'
            case 'MB2.2_pull':
                return 'MB2.2_issuedate'
            case 'MB2.3_pull':
                return 'MB2.3.1'
            case 'root':
                return 'MB4_specific'
        }
    }

    // Based on the arrival flow, return the step to trigger if the user didn't find the shoot in the list
    const pullShootsTriggerNotFound = (flowSource: string) => {
        switch (flowSource) {
            case 'MB1_yes_pull':
                return 'MB1_yes_notfound'
            case 'MB1_no_pull': //mb1.2 e mb1.3
                return 'MB1_no_notfound'
            case 'MB1.4_pull':
                return 'MB5'
            case 'MB2.2_pull':
                return 'MB2.2_notfound'
            case 'MB2.3_pull':
                return 'MB2.3_notfound'
            case 'root':
                return 'MB6'
            default:
                return 'MB4'
        }
    }

    // Get shoot data based on the code:
    const getShootData = async (code: number) => {
        try {
            const {data} = await httpGetAssignment({
                urlParams: {assignmentCode: code ?? raise('The code is not provided')},
                params: {response_type: 'extended'}
            })
            const assignment: Assignment = data
            setSelectedAssignment(assignment)
            addStepToChatHistory({
                id: flowSource,
                source: CHATBOT_SOURCES.USER,
                text: `[Selected shoot ID: ${assignment?.id}]`
            })
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            await chatbotRef?.current?.triggerNextStep({
                value: assignment,
                trigger:
                    assignment.task_media.name === 'walkthrough'
                        ? 'walkthrough_support'
                        : pullShootsTriggerFound(flowSource)
            })
        } catch (e) {
            errorHandler(e)
        }
    }

    const onSelectShoot = (shoot: Assignment) => {
        if (selectedShoot) return
        setSelectedShoot(shoot)
        getShootData(shoot?.code)
    }

    //I don't see my shoot logic
    const onNotListedClick = async () => {
        if (selectedShoot) return
        setSelectedShoot(null)
        setSelectedAssignment(null)
        addStepToChatHistory({
            id: flowSource,
            source: CHATBOT_SOURCES.USER,
            text: `[Selected "The shoot is not listed"]`
        })
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        await chatbotRef?.current?.triggerNextStep({
            value: null,
            trigger: pullShootsTriggerNotFound(flowSource)
        })
    }

    return (
        <div className="bg-neutral-200 p-4 shadow-[0_4px_8px_0_rgb(0_0_0_/_15%)] rounded-lg grid gap-3 [&.not-listed-button]:pointer-events-none">
            {isLoading ? (
                <p> {t('chatbot:pull_shoots:loading')} </p>
            ) : (
                <>
                    {!selectedShoot &&
                        shoots?.map((shoot, index) => (
                            <div className={shootClassName} key={index} onClick={() => onSelectShoot(shoot)}>
                                <span>
                                    {t('chatbot:pull_shoots:airbnb_id')}
                                    {shoot?.entity?.external_id}
                                </span>
                                <p>
                                    <span>{t('chatbot:pull_shoots:host_name')}</span>
                                    <strong>{shoot?.host?.first_name}</strong>
                                </p>
                                <p>
                                    <span>{t('chatbot:pull_shoots:media')}</span>
                                    <strong>{capitalize(shoot?.task_media?.name)}</strong>
                                </p>
                            </div>
                        ))}
                    {selectedShoot && (
                        <div className={cn(shootClassName, 'pointer-events-none')}>
                            <span>
                                {t('chatbot:pull_shoots:airbnb_id')}
                                {selectedShoot?.entity?.external_id}
                            </span>
                            <p>
                                <span> {t('chatbot:pull_shoots:host_name')}</span>{' '}
                                <strong>{selectedShoot?.host?.first_name}</strong>
                            </p>
                            <p>
                                <span>{t('chatbot:pull_shoots:media')}</span>{' '}
                                <strong>{capitalize(selectedShoot?.task_media?.name)}</strong>
                            </p>
                        </div>
                    )}
                    {!selectedShoot && (
                        <Button
                            variant={'primary'}
                            className="w-full"
                            onClick={onNotListedClick}
                            style={{marginTop: shoots?.length > 0 ? 16 : 0}}
                        >
                            {t('chatbot:pull_shoots:shoot_not_listed')}
                        </Button>
                    )}
                </>
            )}
        </div>
    )
}
